import { MediaFrame, ScrollReveal, SplitReveal, TiltCard } from '@/components/motion';
import { GALLERY_ITEMS } from '@/data/experience';
import { cn } from '@ibas/ui';

/** Mosaic span hint → Tailwind col/row spans (dense flow backfills any gaps). */
const SPAN_CLASS: Record<'wide' | 'tall' | 'normal', string> = {
  wide: 'col-span-2 row-span-1',
  tall: 'col-span-1 row-span-2',
  normal: 'col-span-1 row-span-1',
};

/**
 * Per-tile curation so every image reads well under object-cover: an
 * orientation-matched source (portrait photos for the tall tile, landscape for
 * the wide tiles) plus a sensible object-position to protect faces. Keyed by the
 * original GALLERY_ITEMS src; `src`/`alt` override only where the stock choice
 * was orientation-mismatched. All swaps stay within the real IBAS media library.
 */
const TILE_CURATION: Record<string, { src?: string; alt?: string; position?: string }> = {
  // Wide landscape tile — small-group discussion already reads landscape; lift a touch.
  '/media/images/campus/classroom-small-group-discussion.webp': { position: '50% 40%' },
  // Tall PORTRAIT tile held a wide landscape lecture room (heavy side-crop) —
  // swap to a vertical shot of an instructor presenting to fill the tall frame.
  '/media/images/campus/ibas-classroom-lecture-session.webp': {
    src: '/media/images/people/faculty-presenting-classroom.webp',
    alt: 'An instructor presenting to a class while standing in an IBAS classroom.',
    position: '50% 28%',
  },
  // Square tile — portrait building cropped to a square keeps the middle floors.
  '/media/images/campus/ibas-thalwil-building-exterior.webp': { position: '50% 40%' },
  // Wide landscape tile — keep the graduates' faces high in frame.
  '/media/images/graduation-events/graduation-ceremony-group-celebration.webp': {
    position: '50% 35%',
  },
};

/**
 * ExperienceGallery (/experience §6) — "Moments from campus and community".
 *
 * A responsive bento mosaic of the GALLERY_ITEMS: each tile is a MediaFrame
 * (lazy image + navy overlay) wrapped in a TiltCard for a premium pointer lean —
 * permitted here because these are IMAGE tiles, never text cards. Span hints
 * ('wide' | 'tall' | 'normal') drive the col/row spans over a dense grid so the
 * layout packs without holes; each caption sits over a bottom scrim as a white
 * uppercase label with a gold accent (white-on-navy is allowed; white-on-gold is
 * not). The grid enters as a ScrollReveal child-stagger.
 *
 * Motion: SplitReveal headline, ScrollReveal stagger for the tiles, per-tile 3D
 * TiltCard hover. All primitives are reduced-motion safe and never strand
 * content hidden.
 *
 * Sourcing: every image, alt and caption comes verbatim from GALLERY_ITEMS —
 * verified demo/stock assets (the media exception) pending IBAS-owned
 * photography. Nothing here is invented.
 */
export function ExperienceGallery() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Gallery
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Moments from campus and community.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              A glimpse of everyday life at IBAS — study, discussion and the moments in between.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Mosaic ──────────────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.09}
          className="mt-12 grid grid-flow-row-dense grid-cols-2 gap-4 auto-rows-[168px] md:grid-cols-4 md:auto-rows-[188px]"
        >
          {GALLERY_ITEMS.map((item) => {
            const curated = TILE_CURATION[item.src];
            return (
            <div key={item.src} className={cn('h-full', SPAN_CLASS[item.span ?? 'normal'])}>
              <TiltCard max={6} perspective={1000} className="h-full">
                <MediaFrame
                  image={curated?.src ?? item.src}
                  alt={curated?.alt ?? item.alt}
                  aspect="h-full"
                  overlay={38}
                  reveal={false}
                  rounded="rounded-2xl"
                  className="h-full"
                  position={curated?.position}
                >
                  {/* Bottom scrim keeps the caption legible over any image. */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-navy/80 to-transparent"
                  />
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-2.5 p-5">
                    <span aria-hidden="true" className="h-4 w-0.5 shrink-0 rounded-full bg-brand-gold" />
                    <span className="font-label text-xs tracking-[0.18em] text-white uppercase">
                      {item.caption}
                    </span>
                  </div>
                </MediaFrame>
              </TiltCard>
            </div>
            );
          })}
        </ScrollReveal>
      </div>
    </section>
  );
}
